import { useState, useMemo } from 'react';
import { Typography, Card, Row, Col, Button, Spin, Table, Collapse } from 'antd';
import { AreaChartOutlined, LineChartOutlined, BarChartOutlined, TableOutlined } from '@ant-design/icons';
import { LineChart, BarChart, AreaChart, Line, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer, Area } from 'recharts';
import './sales-trends.css';

const { Title, Text } = Typography;

const SalesTrends = ({ data, loading, selectedYear, currentDateInfo }) => {
  const [chartType, setChartType] = useState('line');
  const [showData, setShowData] = useState(false);
  const salesData = useMemo(() => data?.sales_by_month || [], [data]);

  const chartData = useMemo(() => {
    return salesData.map((monthData) => ({
      name: monthData.month,
      MRO: monthData.MRO || 0,
      SER: monthData.SER || 0,
      Desconocido: monthData.Desconocido || 0,
      Total: (monthData.MRO || 0) + (monthData.SER || 0) + (monthData.Desconocido || 0)
    }));
  }, [salesData]);

  const colors = {
    MRO: '#1890ff',
    SER: '#52c41a',
    Desconocido: '#fa8c16',
    Total: '#722ed1'
  };

  const statistics = useMemo(() => {
    if (salesData.length === 0) return null;

    const totals = salesData.reduce((acc, month) => ({
      MRO: acc.MRO + (month.MRO || 0),
      SER: acc.SER + (month.SER || 0),
      Desconocido: acc.Desconocido + (month.Desconocido || 0),
    }), { MRO: 0, SER: 0, Desconocido: 0 });

    const totalGeneral = totals.MRO + totals.SER + totals.Desconocido;
    const promedioMensual = totalGeneral / salesData.length;

    const mesMax = salesData.reduce((max, month) => {
      const monthTotal = (month.MRO || 0) + (month.SER || 0) + (month.Desconocido || 0);
      return monthTotal > max.total ? { month: month.month, total: monthTotal } : max;
    }, { month: '', total: 0 });

    const mesMin = salesData.reduce((min, month) => {
      const monthTotal = (month.MRO || 0) + (month.SER || 0) + (month.Desconocido || 0);
      return monthTotal < min.total ? { month: month.month, total: monthTotal } : min;
    }, { month: '', total: Infinity });

    const crecimiento = salesData.length > 1 ?
      ((salesData[salesData.length - 1].Total - salesData[0].Total) / salesData[0].Total * 100).toFixed(1) : 0;

    return {
      totalGeneral,
      promedioMensual,
      mesMax,
      mesMin,
      totals,
      crecimiento
    };
  }, [salesData]);

  const renderChart = () => {
    const commonProps = {
      data: chartData,
      margin: { top: 10, right: 30, left: 0, bottom: 0 }
    };

    switch (chartType) {
      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12 }}
              />
              <YAxis
                tickFormatter={(value) => `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                tick={{ fontSize: 12 }}
              />
              <RechartsTooltip
                formatter={(value) => [`$${parseFloat(value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, '']}
                labelFormatter={(label) => `Mes: ${label}`}
              />
              <Legend />
              <Bar
                dataKey="MRO"
                name="MRO"
                fill={colors.MRO}
                radius={[2, 2, 0, 0]}
              />
              <Bar
                dataKey="SER"
                name="SER"
                fill={colors.SER}
                radius={[2, 2, 0, 0]}
              />
              <Bar
                dataKey="Total"
                name="Total"
                fill={colors.Total}
                radius={[2, 2, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12 }}
              />
              <YAxis
                tickFormatter={(value) => `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                tick={{ fontSize: 12 }}
              />
              <RechartsTooltip
                formatter={(value) => [`$${parseFloat(value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, '']}
                labelFormatter={(label) => `Mes: ${label}`}
              />
              <Legend />
              <Area
                type="monotone"
                dataKey="Total"
                name="Total"
                stroke={colors.Total}
                fill={colors.Total}
                fillOpacity={0.3}
              />
              <Area
                type="monotone"
                dataKey="MRO"
                name="MRO"
                stroke={colors.MRO}
                fill={colors.MRO}
                fillOpacity={0.3}
              />
              <Area
                type="monotone"
                dataKey="SER"
                name="SER"
                stroke={colors.SER}
                fill={colors.SER}
                fillOpacity={0.3}
              />
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'line':
      default:
        return (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart {...commonProps}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="name"
                tick={{ fontSize: 12 }}
              />
              <YAxis
                tickFormatter={(value) => `$${value.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                tick={{ fontSize: 12 }}
              />
              <RechartsTooltip
                formatter={(value) => [`$${parseFloat(value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, '']}
                labelFormatter={(label) => `Mes: ${label}`}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="Total"
                name="Total"
                stroke={colors.Total}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="MRO"
                name="MRO"
                stroke={colors.MRO}
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="SER"
                name="SER"
                stroke={colors.SER}
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        );
    }
  };

  return (
    <Card className="dashboard-card">
      <div className="card-header">
        <div className="card-icon card-icon-primary">
          <AreaChartOutlined />
        </div>
        <div>
          <Title level={4}>Tendencias de Ventas</Title>
          <Text type="secondary">{currentDateInfo}</Text>
        </div>

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="chart-type-selector">
            <Button
              type={chartType === 'line' ? 'primary' : 'default'}
              size="small"
              onClick={() => setChartType('line')}
              icon={<LineChartOutlined />}
            >
              Líneas
            </Button>
            <Button
              type={chartType === 'bar' ? 'primary' : 'default'}
              size="small"
              onClick={() => setChartType('bar')}
              icon={<BarChartOutlined />}
            >
              Barras
            </Button>
            <Button
              type={chartType === 'area' ? 'primary' : 'default'}
              size="small"
              onClick={() => setChartType('area')}
              icon={<AreaChartOutlined />}
            >
              Área
            </Button>
          </div>
        </div>
      </div>

      <div className="card-content">
        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
            <Spin size="large" />
          </div>
        ) : salesData.length > 0 ? (
          <>
            {statistics && (
              <div className="sales-statistics" style={{ marginBottom: 24 }}>
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12} md={6}>
                    <div className="statistic-card">
                      <div className="statistic-label">Total Año</div>
                      <div className="statistic-value">
                        ${parseFloat(statistics.totalGeneral).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <div className="statistic-card">
                      <div className="statistic-label">Promedio Mensual</div>
                      <div className="statistic-value">
                        ${parseFloat(statistics.promedioMensual).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <div className="statistic-card">
                      <div className="statistic-label">Mejor Mes</div>
                      <div className="statistic-value" style={{ fontSize: '14px' }}>
                        {statistics.mesMax.month}:
                        <span style={{ color: '#52c41a', marginLeft: 4 }}>
                          ${parseFloat(statistics.mesMax.total).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </div>
                  </Col>
                  <Col xs={24} sm={12} md={6}>
                    <div className="statistic-card">
                      <div className="statistic-label">Distribución</div>
                      <div className="statistic-value" style={{ fontSize: '12px' }}>
                        MRO: {((statistics.totals.MRO / statistics.totalGeneral) * 100).toFixed(1)}% |
                        SER: {((statistics.totals.SER / statistics.totalGeneral) * 100).toFixed(1)}%
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>
            )}
            {renderChart()}
            <div style={{ marginTop: 24 }}>
              <Collapse
                ghost
                size="small"
                activeKey={showData ? ['1'] : []}
                onChange={(keys) => setShowData(keys.length > 0)}
              >
                <Collapse.Panel
                  header={
                    <span>
                      <TableOutlined /> Ver datos detallados por mes
                    </span>
                  }
                  key="1"
                  style={{ border: '1px solid #f0f0f0', borderRadius: '8px' }}
                >
                  <Table
                    dataSource={chartData}
                    columns={[
                      {
                        title: 'Mes',
                        dataIndex: 'name',
                        key: 'name',
                      },
                      {
                        title: 'MRO',
                        dataIndex: 'MRO',
                        key: 'MRO',
                        render: (value) => `$${parseFloat(value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      },
                      {
                        title: 'SER',
                        dataIndex: 'SER',
                        key: 'SER',
                        render: (value) => `$${parseFloat(value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                      },
                      {
                        title: 'Total',
                        dataIndex: 'Total',
                        key: 'Total',
                        render: (value) => (
                          <Text strong>
                            ${parseFloat(value).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </Text>
                        )
                      }
                    ]}
                    pagination={false}
                    size="small"
                    style={{ marginTop: 16 }}
                  />
                </Collapse.Panel>
              </Collapse>
            </div>
          </>
        ) : (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: 300,
            flexDirection: 'column',
            color: '#999'
          }}>
            <BarChartOutlined style={{ fontSize: 48, marginBottom: 16 }} />
            <Text type="secondary">No hay datos de tendencias disponibles</Text>
            <Text type="secondary" style={{ fontSize: 12, marginTop: 8 }}>
              Seleccione un período diferente o verifique los datos
            </Text>
          </div>
        )}
      </div>
    </Card>
  );
};

export default SalesTrends;